import { ContentState } from "draft-js";
import { List, Map } from "immutable";
import { SimpleMappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimplePaginatedStore } from "@core/store/paginatedStore";
import { SimpleStore } from "@core/store/store";
import { Page } from "@utils/page";
import { Media } from "@domain/media/media";
import { Post } from "@domain/post/post";
import { Accreditation } from "./accreditation";
import { Organization, OrganizationMemberRole } from "./organization";
import { OrganizationApi } from "./organizationApi";
import { OrganizationDraft } from "./organizationDraft";
import { Track, TrackDraft } from "@domain/organization/track/track";
import { TrackService } from "@domain/organization/track/trackService";

export class OrganizationService {
	organizationStore = new SimpleStore((id) => this.organizationApi.get(id));
	slugOrganizationStore = new SimpleStore<Organization>((slug) => this.organizationApi.getBySlug(slug), {
		primaryKey: "slug",
	});
	userOrganizations = new SimplePaginatedStore((page) => this.organizationApi.getMyOrganizations(page));
	accreditationsStore = new SimpleMappedStore((id) => this.organizationApi.getAccreditations(id));
	assetsStore = new SimpleMappedStore((id, page) => this.organizationApi.getAssets(id, page));
	tracksStore = new SimpleMappedStorePresentation(this.trackService.trackStore, (id) =>
		this.organizationApi.getTracks(id)
	);
	organizationShareroomsStore = new SimpleMappedStore((organizationId, page) =>
		this.organizationApi.getOrganizationShareroom(organizationId, page)
	);

	constructor(
		private readonly organizationApi: OrganizationApi,
		private readonly trackService: TrackService,
		initialOrganizations?: Map<string, Organization>
	) {
		if (initialOrganizations) {
			this.organizationStore.merge(List(initialOrganizations.values()));
		}
		this.slugOrganizationStore.bind(this.organizationStore);
	}

	async save(draft: OrganizationDraft, id?: string): Promise<Organization> {
		const organization = await (id ? this.organizationApi.save(draft, id) : this.organizationApi.create(draft));
		this.organizationStore.save(organization);
		this.userOrganizations.fetch();
		return organization;
	}

	async delete(id: string): Promise<void> {
		await this.organizationApi.delete(id);
		this.userOrganizations.fetch();
	}

	async saveAccreditation(
		organizationId: string,
		newAccreditation: Accreditation,
		accreditationId?: string
	): Promise<void> {
		const accreditation = accreditationId
			? await this.organizationApi.saveAccreditation(newAccreditation)
			: await this.organizationApi.createAccreditation(organizationId, newAccreditation);

		this.accreditationsStore.updateContent(organizationId, (accreditations) =>
			accreditationId
				? accreditations.map((acc) => (acc.id === accreditationId ? accreditation : acc))
				: accreditations.push(accreditation)
		);
	}

	async orderAccreditation(accreditations: Accreditation[], organizationId: string): Promise<void> {
		await this.organizationApi.orderAccreditation(accreditations, organizationId);
	}

	async deleteAccreditation(organizationId: string, id: string): Promise<void> {
		await this.organizationApi.deleteAccreditation(id);
		this.accreditationsStore.updateContent(organizationId, (accreditations) =>
			accreditations.remove(accreditations.findIndex((acc) => acc.id === id))
		);
	}

	async saveTrack(organizationId: string, draft: TrackDraft, trackId?: string): Promise<Track> {
		if (trackId) {
			return this.trackService.save(draft, trackId);
		}
		const track = await this.organizationApi.createTrack(organizationId, draft);
		this.tracksStore.fetch(organizationId);
		return track;
	}

	async deleteTrack(organization: Organization, id: string): Promise<void> {
		const org = await this.organizationApi.save(
			{
				...organization,
				tracks: organization.tracks?.filter((track) => track.id !== id),
			},
			organization.id
		);
		this.organizationStore.save(org);
	}

	async savePresentation(
		organization: Organization,
		presentation: ContentState,
		presentationAsset?: { url?: string; video?: Media }
	): Promise<void> {
		const org = await this.organizationApi.save(
			{
				...organization,
				presentation,
				presentationAsset,
			},
			organization.id
		);
		this.organizationStore.save(org);
	}

	suggest = (count: number): Promise<Page<Organization>> => {
		return this.organizationApi.getSuggestions({ page: 0, size: count });
	};

	async addAsset(organizationId: string, newAssets: Media[]): Promise<void> {
		await this.organizationApi.addAsset(
			organizationId,
			newAssets.map((asset) => asset.id)
		);
		this.assetsStore.updateContent(organizationId, (assets) => assets.concat(newAssets));
	}

	async deleteAsset(organizationId: string, id: string): Promise<void> {
		await this.organizationApi.deleteAsset(organizationId, id);
		this.assetsStore.updateContent(organizationId, (assets) => assets.remove(assets.findIndex((acc) => acc.id === id)));
	}

	async shareOrganization(organizationId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.organizationApi.share(organizationId, message, groupId);
	}

	updateMemberCount(organizationId: string, role: OrganizationMemberRole, delta: number): void {
		this.organizationStore.update(organizationId, (org) => ({
			...org,
			memberCounts: { ...org.memberCounts, [role]: (org.memberCounts[role] ?? 0) + delta },
		}));
	}

	search = this.organizationApi.search.bind(this.organizationApi);
}
